import { getBinding, getScreenId } from "./binding";
import type { EngagementEvent } from "@shared/types/engagement";
import type { ContentPlayEvent } from "@shared/types/contentPlay";

/**
 * Content-engagement tracking for the in-room TV.
 *
 * A queue-and-flush client, deliberately the same shape as the guest
 * companion's (`src/guest/analytics.ts`):
 *   - events are buffered and sent in batches, so a guest arrowing through the
 *     Apps grid doesn't generate a request per keypress,
 *   - the flush is `sendBeacon` where available, so the last batch still lands
 *     when the TV navigates away into a streaming app (which is precisely the
 *     event we most want to record),
 *   - every failure is swallowed. Analytics must never break a guest screen.
 *
 * It carries no guest identity: the payload is event type + a content id, and
 * the server attributes it to the premise via the screen token alone.
 *
 * ## Two payloads, one channel (`ANALYTICS-2`)
 *
 * Content PLAY events ride here too, in a second array of the same body. They
 * are a different model with a different grain - see `@shared/types/contentPlay`
 * - and they are deliberately NOT folded into `EngagementEvent`: an asset id has
 * to round-trip to an external catalogue exactly, and `kind` has to stay part of
 * the row key. What they share is the TRANSPORT, and that is the part worth
 * having once: the batching, the fifteen-second timer, the `sendBeacon` on the
 * way out, the screen token, and the rule that a failure here never reaches a
 * guest's screen. A second endpoint would be a second thing to authenticate,
 * rate-limit and lose on an offline panel.
 */

/** Flush once the buffer reaches this many events. */
const BATCH_SIZE = 20;
/** …or after this long, whichever comes first. */
const FLUSH_INTERVAL_MS = 15_000;
/** Hard ceiling so an offline TV can't grow the buffer without bound. */
const MAX_QUEUE = 200;

let queue: EngagementEvent[] = [];
/** Play reports, batched beside the engagement events and never merged. */
let plays: ContentPlayEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let listenersBound = false;

/** How much is waiting, across both payloads - what the batch rule counts. */
const queued = () => queue.length + plays.length;

/** The ingest URL for this screen, or null when the TV isn't paired yet. */
function endpoint(): { url: string; token: string } | null {
  const binding = getBinding();
  const screenId = getScreenId();
  if (!binding?.screenToken || !screenId) return null;
  return {
    url: `/api/foyer/screens/${screenId}/engagement`,
    token: binding.screenToken,
  };
}

/**
 * Ship whatever is queued. `beacon: true` uses `navigator.sendBeacon`, which
 * survives the page being torn down - but it can't set the `X-Screen-Token`
 * header, so the token rides as a query param there (the screen-auth middleware
 * accepts both, for exactly this reason).
 */
export function flushEngagement(beacon = false): void {
  if (!queued()) return;
  const target = endpoint();
  if (!target) return; // unpaired - drop rather than buffer forever
  const events = queue;
  const playEvents = plays;
  queue = [];
  plays = [];
  const body = JSON.stringify({ events, plays: playEvents });
  try {
    if (beacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      const url = `${target.url}?screen_token=${encodeURIComponent(target.token)}`;
      const ok = navigator.sendBeacon(
        url,
        new Blob([body], { type: "application/json" })
      );
      if (ok) return;
      // sendBeacon refused (queue full / too large) - fall through to fetch.
    }
    void fetch(target.url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Screen-Token": target.token,
      },
      body,
      keepalive: true,
    }).catch(() => {
      /* best-effort */
    });
  } catch {
    /* best-effort */
  }
}

/** Bind the "we're about to lose the page" flush hooks, exactly once. */
function bindLifecycle(): void {
  if (listenersBound || typeof document === "undefined") return;
  listenersBound = true;
  // `pagehide` covers a real teardown; `visibilitychange` covers the TV being
  // backgrounded when a guest launches a streaming app, which on most TV
  // platforms is the only signal we get.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushEngagement(true);
  });
  window.addEventListener("pagehide", () => flushEngagement(true));
}

function scheduleFlush(): void {
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    flushEngagement();
  }, FLUSH_INTERVAL_MS);
}

/**
 * Queue one engagement event. Cheap and synchronous - safe to call from a
 * keypress handler or straight before a native app launch.
 *
 * `seconds` is only meaningful on the duration-bearing types (`app_dwell`,
 * `cast_dwell`, `slot_view`) and is dropped when it isn't a positive finite
 * number: a broken clock on one TV must cost that TV its durations, not poison
 * a property's averages with a negative or NaN sum.
 */
export function trackEngagement(
  type: EngagementEvent["type"],
  target?: string,
  label?: string,
  seconds?: number
): void {
  bindLifecycle();
  if (queued() >= MAX_QUEUE) return; // offline / unpaired: stop growing
  const value =
    typeof seconds === "number" && Number.isFinite(seconds) && seconds > 0
      ? Math.round(seconds)
      : undefined;
  queue.push({
    type,
    ...(target ? { target } : {}),
    ...(label ? { label } : {}),
    ...(value !== undefined ? { value } : {}),
    at: Date.now(),
  });
  if (queued() >= BATCH_SIZE) {
    flushEngagement();
    return;
  }
  scheduleFlush();
}

/**
 * Queue one content-play report (`ANALYTICS-2`).
 *
 * The same batching rules as {@link trackEngagement}, and deliberately the same
 * queue budget: a television that has lost its uplink must not be able to grow
 * the two buffers to `MAX_QUEUE` each.
 *
 * Nothing is validated here beyond the shape - `contentPlayStore` sanitises and
 * clamps server-side, because a screen token proves which premise a report came
 * from and nothing at all about the honesty of the numbers inside it.
 */
export function trackContentPlay(event: ContentPlayEvent): void {
  bindLifecycle();
  if (queued() >= MAX_QUEUE) return;
  plays.push(event);
  if (queued() >= BATCH_SIZE) {
    flushEngagement();
    return;
  }
  scheduleFlush();
}

/**
 * Time one visit and report it when it ends.
 *
 * Every "how long" metric in this product is the same three lines - note the
 * start, subtract on the way out, drop the reading if it is implausible - and
 * getting any of them subtly wrong (counting a session that never ended,
 * double-counting a re-entry) shows up as a number a customer is paying for.
 * So it exists once.
 *
 * Returns a function that ends the visit; calling it twice is a no-op, which
 * matters because React cleanups and lifecycle listeners both race to fire it.
 */
export function startDwell(
  type: EngagementEvent["type"],
  target: string,
  label?: string
): () => void {
  const started = Date.now();
  let ended = false;
  return () => {
    if (ended) return;
    ended = true;
    const seconds = (Date.now() - started) / 1000;
    // Sub-second visits are focus flicker, not attention. Reporting them would
    // drag every average toward zero and make a good promotion look ignored.
    if (seconds < 1) return;
    trackEngagement(type, target, label, seconds);
  };
}

/** Test seam - drop anything buffered without sending it. */
export function resetEngagement(): void {
  queue = [];
  plays = [];
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
}
